import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Reports from audio → Body-cam footage. Fixture `bodyCamFootage`: the
// officer has a footage seat (addons.video entitled). The interview sample
// audio is attached under an .mp4 name and a video MIME type so the upload
// modal treats it as video and shows the two footage toggles.
const ROW = '[data-file-index="0"]';

const scene: Scene = {
  id: "body-cam-footage",
  title: "Body-cam footage",
  startRoute: "/cases",
  fixture: "bodyCamFootage",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "You have body-cam video and need a report from it. You upload it like any other recording. The audio is pulled out and transcribed as an interaction, and by default the video file itself is discarded. Two toggles change that.",
      actions: [
        { type: "waitFor", target: sel("newCaseDropdown") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "add-video",
      narration: "I'll click New Case, choose Upload Files, and add the video.",
      actions: [
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 300 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
        { type: "moveTo", target: sel("uploadDropzone") },
        { type: "wait", ms: 600 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/interaction-witness-interview.mp3`,
          name: "bodycam-valley-mart.mp4",
          mime: "video/mp4",
        },
        { type: "waitFor", target: sel("uploadTypeInteraction") },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "interaction",
      narration:
        "The row defaults to Interaction, because a video is a conversation. Add the people on the recording under Speaker Information, and their names will carry through to the draft.",
      actions: [
        { type: "moveTo", target: `${ROW} ${sel("uploadTypeInteraction")}` },
        { type: "spotlight", target: `${ROW} ${sel("uploadTypeInteraction")}` },
        { type: "wait", ms: 5000 },
        { type: "moveTo", target: sel("uploadSpeakerInformation") },
        { type: "spotlight", target: sel("uploadSpeakerInformation") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "keep",
      narration:
        "Keep original video file is off by default. Turn it on if you want to play the video from the case later. It's only available for files of two gigabytes or less. Anything bigger is always processed as audio only.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadKeepVideo") },
        { type: "moveTo", target: sel("uploadKeepVideo") },
        { type: "spotlight", target: sel("uploadKeepVideo") },
        { type: "wait", ms: 9000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "analyze",
      narration:
        "If your department gave you a footage seat, you also see Analyze the footage. I'll turn it on. That lets the AI watch the video, not just listen to it, and it forces Keep original video file on, because the frames are read from the original.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadVideoAnalysis") },
        { type: "moveTo", target: `${sel("uploadVideoAnalysis")} input[type="checkbox"]` },
        { type: "spotlight", target: sel("uploadVideoAnalysis") },
        // "I'll turn it on" is spoken at about 4.6 s.
        { type: "wait", ms: 3800 },
        { type: "click", target: `${sel("uploadVideoAnalysis")} input[type="checkbox"]` },
        { type: "wait", ms: 6000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "modes",
      narration:
        "Then choose how to use it. Check the draft against the footage writes the narrative from the audio and sends any differences as suggestions. Write footage observations into the draft waits for the video and includes what the camera shows. There's also an option to read every minute, which is slower.",
      actions: [
        { type: "waitFor", target: `${sel("uploadVideoAnalysis")} input[type="radio"]` },
        { type: "moveTo", target: `${sel("uploadVideoAnalysis")} label:nth-of-type(1)` },
        { type: "spotlight", target: sel("uploadVideoAnalysis") },
        { type: "wait", ms: 14000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "upload",
      narration:
        "Then I'll click Upload. Long footage goes up from your browser in the background, so stay on the page until the progress finishes.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadSubmit") },
        { type: "wait", ms: 1500 },
        { type: "click", target: sel("uploadSubmit") },
        { type: "waitFor", target: sel("editorProcessing"), timeoutMs: 15000 },
        { type: "wait", ms: 3500 },
        { type: "fixture", name: "completeLatestCase" },
      ],
    },
    {
      id: "draft",
      narration:
        "And here's the draft, written from the audio. For long footage, dictate a short narrative alongside it, so the AI knows what mattered.",
      actions: [
        { type: "waitFor", target: sel("reportScore"), timeoutMs: 15000 },
        { type: "wait", ms: 2500 },
        { type: "spotlight", target: sel("reportScore") },
        { type: "wait", ms: 4000 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
